import {
  ResourceType,
  ComponentBatchDeleteResponse,
  WebSocketDeletePayload,
  WebSocketFieldUnappliedPayload,
  WebSocketGlobalFieldDeletedPayload,
  WebSocketGlobalFieldOptionsRemovedPayload,
  WebSocketGlobalFieldRenamedPayload,
  WebSocketGlobalFieldUpdatedPayload,
  WebSocketRecalculationDonePayload,
  WebSocketRecalculationErrorPayload,
  WebSocketRecalculationStartedPayload,
  NotificationApiResponse,
  VersionedEntity,
  MetaData,
} from '@ardoq/api-types';

export enum WSClosingReason {
  SESSION_EXPIRED = 'session-expired',
  LOGOUT = 'logout',
}
export enum EventType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  BULK_DELETE = 'bulk-delete',
  RECALCULATION_STARTED = 'recalculation-started',
  RECALCULATION_DONE = 'recalculation-done',
  RECALCULATION_FAILED = 'recalculation-failed',
  GLOBAL_RECALCULATION_STARTED = 'global-recalculation-started',
  GLOBAL_RECALCULATION_DONE = 'global-recalculation-done',
  GLOBAL_RECALCULATION_ERROR = 'global-recalculation-error',
  GLOBAL_FIELD_DELETED = 'global-field-deleted',
  GLOBAL_FIELD_UPDATED = 'global-field-updated',
  GLOBAL_FIELD_RENAMED = 'global-field-renamed',
  GLOBAL_FIELD_OPTIONS_REMOVED = 'global-field-options-removed',
  FIELD_UNAPPLIED = 'field-unapplied',
  INFERRED = 'inferred',
  ERROR = 'error',
  RESULT = 'result',
  PROGRESS = 'progress',
  SUGGEST = 'suggest',
  NOTIFICATION = 'notification',
  MESSAGE_CONTENT = 'message-content',
  MESSAGE_DONE = 'message-done',
  MESSAGE_ERROR = 'message-error',
  MESSAGE_TOOL_CALL = 'message-tool-call',
  MESSAGE_TOOL_RESULT = 'message-tool-result',
  CHAT_SESSION_TITLE_GENERATED = 'chat-session-title-generated',
  CHAT_SESSION_CONCLUDED = 'chat-session-concluded',
  CHAT_SESSION_NOTIFICATION_READ = 'chat-session-notification-read',
  AGENT_INTERACTION_REQUESTED = 'agent-interaction-requested',
  AGENT_INTERACTION_RESOLVED = 'agent-interaction-resolved',
  PLAN_PROPOSED = 'plan-proposed',
  TASK_STARTED = 'task-started',
  TASK_FINISHED = 'task-finished',
  EXECUTION_FAILED = 'execution-failed',
  ORCHESTRATION_CONCLUDED = 'orchestration-concluded',
  ASYNC_REQUEST_PROGRESS = 'async-request-progress',
  BUNDLE_COPY = 'bundle-copy',
  WARNING = 'warning',
  SUCCESS = 'success',
  UNSUPPORTED = 'unsupported',
  GREMLIN = 'gremlin',
  FIELD_ACCESS_CONTEXT_CHANGED = 'field-access-context-changed',
  AUTO_LINK_FINISHED = 'auto-link-finished',
}

type PingEvent = 'Ping!';

export type TokenEvent = {
  token: string;
};

type ExpiredEvent = 'Expired!';

type SessionExpiredEvent = {
  topic: 'session-expired';
  data: {
    ['session-id']: string;
  };
};

type LoggedOutEvent = {
  topic: 'logout';
};

export type ArdoqEvent<Data = unknown> = {
  token: string;
  transactionEvent?: boolean;
  ['resource-type']: ResourceType;
  ['event-type']: EventType;
  data: Data;
  branchId?: string;
  org?: { label: string; _id: string };
  user?: { name: string; id: string };
};

export type OrgEvent<Data = unknown> = ArdoqEvent<Data> & {
  transactionEvent?: true;
  topic: string;
  user: string | null;
};

export type UserEvent<Data = unknown> = ArdoqEvent<Data> & {
  topic: string;
  user: string | null;
};

export type WorkspaceEvent<Data = unknown> = ArdoqEvent<Data> & {
  topic: string;
  user?: { name: string; id: string };
};

type BatchEvent<Data = unknown> = ArdoqEvent<Data> & {
  topic: 'batch';
  user: { name: string; id: string };
};

export type UnknownEvent =
  | PingEvent
  | TokenEvent
  | ExpiredEvent
  | SessionExpiredEvent
  | LoggedOutEvent
  | OrgEvent
  | UserEvent
  | WorkspaceEvent
  | BatchEvent;

export type ClientRequest<Data> = {
  clientRequestId?: string;
  exchangeId?: string;
} & Data;

export type MessageEvent = {
  message: string;
};

export const isClientRequestErrorEvent = (
  event: ArdoqEvent<unknown>
): event is UserEvent<ClientRequest<MessageEvent>> =>
  event['event-type'] === EventType.ERROR;

export const isWebSocketCreate = (event: ArdoqEvent<unknown>): boolean =>
  event['event-type'] === EventType.CREATE;

export const isWebSocketUpdate = (event: ArdoqEvent<unknown>): boolean =>
  event['event-type'] === EventType.UPDATE;

export const isWebSocketInferred = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<unknown> => event['event-type'] === EventType.INFERRED;

export const isWebSocketWarning = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<unknown> => event['event-type'] === EventType.WARNING;

export const isWebSocketSuggest = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<unknown> => event['event-type'] === EventType.SUGGEST;

export const isWebSocketDelete = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<WebSocketDeletePayload> =>
  event['event-type'] === EventType.DELETE;

export const isWebSocketBulkDelete = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<ComponentBatchDeleteResponse> =>
  event['event-type'] === EventType.BULK_DELETE;

export const isWebSocketGlobalFieldDeleted = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<WebSocketGlobalFieldDeletedPayload> =>
  event['event-type'] === EventType.GLOBAL_FIELD_DELETED;

export const isWebSocketGlobalFieldUpdated = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<WebSocketGlobalFieldUpdatedPayload> =>
  event['event-type'] === EventType.GLOBAL_FIELD_UPDATED;

export const isWebSocketGlobalFieldRenamed = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<WebSocketGlobalFieldRenamedPayload> =>
  event['event-type'] === EventType.GLOBAL_FIELD_RENAMED;

export const isWebSocketGlobalFieldOptionsRemoved = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<WebSocketGlobalFieldOptionsRemovedPayload> =>
  event['event-type'] === EventType.GLOBAL_FIELD_OPTIONS_REMOVED;

export const isWebSocketFieldUnapplied = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<WebSocketFieldUnappliedPayload> =>
  event['event-type'] === EventType.FIELD_UNAPPLIED;

export const isWebSocketRecalculationStarted = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<WebSocketRecalculationStartedPayload> =>
  event['event-type'] === EventType.RECALCULATION_STARTED;

export const isWebSocketRecalculationDone = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<WebSocketRecalculationDonePayload> =>
  event['event-type'] === EventType.RECALCULATION_DONE;

export const isWebSocketRecalculationError = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<WebSocketRecalculationErrorPayload> =>
  event['event-type'] === EventType.RECALCULATION_FAILED;

export const isWebSocketGlobalRecalculationStarted = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<WebSocketRecalculationStartedPayload> =>
  event['event-type'] === EventType.GLOBAL_RECALCULATION_STARTED;

export const isWebSocketGlobalRecalculationDone = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<WebSocketRecalculationDonePayload> =>
  event['event-type'] === EventType.GLOBAL_RECALCULATION_DONE;

export const isWebSocketGlobalRecalculationError = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<WebSocketRecalculationErrorPayload> =>
  event['event-type'] === EventType.GLOBAL_RECALCULATION_ERROR;

export const isWebSocketNotification = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<NotificationApiResponse> => {
  return event['event-type'] === EventType.NOTIFICATION;
};

export const isResourceCreate = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<VersionedEntity & MetaData> =>
  event['event-type'] === EventType.CREATE;

export const isResourceUpdate = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<VersionedEntity & MetaData> =>
  event['event-type'] === EventType.UPDATE;

export const isResourceDelete = (
  event: ArdoqEvent<unknown>
): event is ArdoqEvent<VersionedEntity> =>
  event['event-type'] === EventType.DELETE;

export const isBundleCopy = (event: ArdoqEvent<unknown>): boolean =>
  event['event-type'] === EventType.BUNDLE_COPY;
